import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import jwtDecode from 'jwt-decode';

import {SocialService} from './social.service';
import {AuthEventsEmitter} from '../events/auth.events';
import {LoginDTO} from '../dto/login.dto';
import {RegisterDTO} from '../dto/register.dto';
import {LoginResponseViewModel} from '../dto/loginResponse.dto';
import {FacebookLoginDTO} from '../dto/facebookLogin.dto';
import {SocialProviders} from '../enums/socialProviders';
import {Constants} from '../config/constants.config';
import {JWTPayload} from '../models/jwtPayload.model';
import {StorageService} from './storage.service';
import {AppLoadingEventsEmitter} from '../events/loading.events';
import {NotificationsService} from './notification.service';
import {LocalisationService} from './localisation.service';
import {Languages} from '../enums/languages';
import {CreatePasswordResetLinkDTO} from '../dto/createPasswordResetLink.dto';
import {ResetPasswordDTO} from '../dto/resetPassword.dto';

@Injectable()
export class AuthService {
    private readonly JWT_KEY: string = 'JWT_KEY';

    token: string;
    userHasLoggedIn: boolean;

    constructor(
        private readonly socialService: SocialService,
        private readonly authEventsEmitter: AuthEventsEmitter,
        private readonly httpClient: HttpClient,
        private readonly constants: Constants,
        private readonly storageService: StorageService,
        private readonly router: Router,
        private readonly appIsLoadingEventsEmitter: AppLoadingEventsEmitter,
        private readonly notificationService: NotificationsService,
        private readonly localisationService: LocalisationService
    ) {
        this.token = this.getJWT();
        this.userHasLoggedIn = !!this.token || this.socialService.userIsLoggedInFacebook;

        this.authEventsEmitter.tokenChanged$.subscribe(token => {
            this.token = token;
            this.userHasLoggedIn = !!this.token || this.socialService.userIsLoggedInFacebook;

            if (token) {
                this.setJWT(token);
            } else {
                this.deleteJWT();
            }
        });
    }

    login(email: string, password: string): Observable<LoginResponseViewModel> {
        return this.httpClient
            .post<LoginResponseViewModel>(`${this.constants.apiURL()}/authorize/login/`, {
                email,
                password
            } as LoginDTO)
            .pipe(
                map(({token}) => {
                    this.authEventsEmitter.broadcastTokenChanged(token);

                    return {token} as LoginResponseViewModel;
                })
            );
    }

    facebookLogin(email: string, first_name: string, last_name: string, account_id: string, locale: Languages, referral: string): Observable<LoginResponseViewModel> {
        return this.httpClient
            .post<LoginResponseViewModel>(`${this.constants.apiURL()}/authorize/facebook/`, {
                email,
                first_name,
                last_name,
                provider: {
                    account_id,
                    provider: SocialProviders.Facebook
                },
                locale,
                referral
            } as FacebookLoginDTO)
            .pipe(
                map(({token}) => {
                    this.authEventsEmitter.broadcastTokenChanged(token);

                    return {token} as LoginResponseViewModel;
                })
            );
    }

    register(registerDTO: RegisterDTO): Observable<LoginResponseViewModel> {
        return this.httpClient.post(`${this.constants.apiURL()}/authorize/register/`, registerDTO).pipe(
            switchMap(() => {
                return this.login(registerDTO.email, registerDTO.password);
            })
        );
    }

    createPasswordResetLink(createPasswordResetLinkDTO: CreatePasswordResetLinkDTO): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/reset/password/`, createPasswordResetLinkDTO);
    }

    resetPassword(resetPasswordDTO: ResetPasswordDTO): Observable<void> {
        return this.httpClient.put<void>(`${this.constants.apiURL()}/reset/password/`, resetPasswordDTO);
    }

    getJWT(): string {
        return this.storageService.getItem(this.JWT_KEY);
    }

    setJWT(jwt: string): void {
        this.storageService.setItem(this.JWT_KEY, jwt);
    }

    deleteJWT(): void {
        this.storageService.removeItem(this.JWT_KEY);
    }

    logout() {
        this.authEventsEmitter.broadcastTokenChanged(null);

        this.router.navigate(['login']).then(() => {
            this.appIsLoadingEventsEmitter.broadcastAppIsLoading(false);
        });
    }

    setStorageItem(key, value) {
        this.storageService.setItem(key, value);
    }

    getStorageItem(key) {
        return this.storageService.getItem(key);
    }

    errorHandler() {
        this.appIsLoadingEventsEmitter.broadcastAppIsLoading(false);

        this.logout();

        this.notificationService.error(this.localisationService.localise('server_err_something_went_wrong'));
    }

    get jwtPayload(): JWTPayload {
        if (!this.token) {
            return undefined;
        }

        return jwtDecode(this.token);
    }
}
